/**
 * Client-side Face Detection Service
 * Uses @vladmandic/human for real-time face detection in the browser
 * Provides instant face-centered crops before server upload
 *
 * Also includes border detection to avoid black/dark bars (screenshots)
 */

import Human from '@vladmandic/human';
import clientLogger from './clientLogger';

// Thresholds for detecting border pixels (black/white bars, screenshot borders)
const DARK_THRESHOLD = 50;   // RGB values below this are considered "dark"
const WHITE_THRESHOLD = 245; // RGB values above this are considered "white"

// Human configuration - minimal setup for face detection only
const humanConfig = {
  // SELF-HOSTED (client/public/models/human/): the exact blazeface files this
  // app used to fetch from vladmandic.github.io at runtime — now first-party,
  // version-pinned, and edge-cacheable. Root-relative so it works on any host.
  modelBasePath: '/models/human/',
  filter: { enabled: false },

  face: {
    enabled: true,
    detector: {
      enabled: true,
      rotation: false,
      maxDetected: 10,
      minConfidence: 0.3,
      return: true,
    },
    mesh: { enabled: false },
    iris: { enabled: false },
    description: { enabled: false },
    emotion: { enabled: false },
    antispoof: { enabled: false },
    liveness: { enabled: false },
  },
  body: { enabled: false },
  hand: { enabled: false },
  object: { enabled: false },
  gesture: { enabled: false },
  segmentation: { enabled: false },
};

// Worker instances run TensorFlow's WASM backend (multi-core CPU, SIMD):
// measured 12 photos in ~3s across 4 workers vs ~5.6s with webgl-in-worker,
// it needs no GPU context per worker (iOS Safari caps WebGL contexts), and it
// scales with cores. The in-thread fallback keeps Human's default (webgl).
// The wasm binaries in /models/tfjs-wasm/ are tfjs 4.22.0 — the build bundled
// inside @vladmandic/human (see dist/tfjs.version.js). Re-download them if the
// human package (and its bundled tfjs) is ever upgraded.
const workerHumanConfig = {
  ...humanConfig,
  backend: 'wasm',
  // SELF-HOSTED (client/public/models/tfjs-wasm/): the tfjs wasm binaries at
  // the pinned version above, formerly fetched from jsdelivr. Root-relative
  // resolves against the page origin inside workers too.
  wasmPath: '/models/tfjs-wasm/',
};

let human = null;
let isInitializing = false;
let initPromise = null;

/**
 * Initialize Human library (singleton pattern)
 * Uses CDN for models to avoid bundling large files
 */
async function initHuman() {
  if (human && human.models?.loaded()) {
    return human;
  }

  if (isInitializing && initPromise) {
    return initPromise;
  }

  isInitializing = true;
  initPromise = (async () => {
    try {
      clientLogger.debug('[FaceDetection] Initializing Human library...');
      human = new Human(humanConfig);
      await human.load();
      await human.warmup('none');
      clientLogger.debug('[FaceDetection] Human library initialized successfully');
      return human;
    } catch (error) {
      console.error('[FaceDetection] Failed to initialize Human:', error);
      isInitializing = false;
      initPromise = null;
      throw error;
    }
  })();

  return initPromise;
}

// ---------------------------------------------------------------------------
// Worker pool: N workers, each with its OWN Human instance -> N photos detect
// truly in parallel. (A single shared engine serializes detect() calls, so a
// concurrency pool on the main thread gave ~no speedup for detection.)
// Falls back to the in-thread singleton when workers can't start.
// ---------------------------------------------------------------------------
const WORKER_POOL_SIZE = Math.max(1, Math.min(4, (navigator.hardwareConcurrency || 4) - 1));
let workerPool = null;       // { workers: [{worker, busy}], queue: [], ready: Promise<boolean> }
let workerPoolDisabled = false;
let requestSeq = 0;

function supportsDetectionWorkers() {
  return typeof Worker !== 'undefined' && typeof OffscreenCanvas !== 'undefined' && typeof ImageData !== 'undefined';
}

function initWorkerPool() {
  if (workerPool) return workerPool.ready;
  if (workerPoolDisabled || !supportsDetectionWorkers()) return Promise.resolve(false);
  const workers = [];
  const readyPromises = [];
  for (let i = 0; i < WORKER_POOL_SIZE; i++) {
    try {
      const worker = new Worker(new URL('./faceDetection.worker.js', import.meta.url));
      const slot = { worker, busy: false, pending: new Map() };
      readyPromises.push(new Promise((resolve) => {
        const onInit = (ev) => {
          const d = ev.data || {};
          if (d.type === 'ready') { worker.removeEventListener('message', onInit); resolve(true); }
          else if (d.type === 'init-error') { worker.removeEventListener('message', onInit); resolve(false); }
        };
        worker.addEventListener('message', onInit);
        worker.addEventListener('error', () => resolve(false));
      }));
      worker.addEventListener('message', (ev) => {
        const d = ev.data || {};
        if (d.type !== 'result' && d.type !== 'error') return;
        const p = slot.pending.get(d.id);
        if (!p) return;
        slot.pending.delete(d.id);
        slot.busy = false;
        if (d.type === 'result') p.resolve(d.faces || []); else p.reject(new Error(d.error || 'worker detect failed'));
        drainWorkerQueue();
      });
      worker.postMessage({ type: 'init', config: workerHumanConfig });
      workers.push(slot);
    } catch (e) {
      // Worker construction failed (CSP / bundler) -> fall back
    }
  }
  if (!workers.length) { workerPoolDisabled = true; return Promise.resolve(false); }
  workerPool = { workers, queue: [], ready: null };
  workerPool.ready = Promise.all(readyPromises).then((flags) => {
    // Drop workers that failed to init; disable pool if none survived
    workerPool.workers = workerPool.workers.filter((_, i) => flags[i]);
    if (!workerPool.workers.length) { workerPoolDisabled = true; workerPool = null; return false; }
    clientLogger.debug(`[FaceDetection] Worker pool ready: ${workerPool.workers.length} worker(s)`);
    drainWorkerQueue();
    return true;
  });
  return workerPool.ready;
}

function drainWorkerQueue() {
  if (!workerPool) return;
  for (const slot of workerPool.workers) {
    if (slot.busy || !workerPool.queue.length) continue;
    const job = workerPool.queue.shift();
    slot.busy = true;
    slot.pending.set(job.id, job);
    slot.worker.postMessage({ type: 'detect', id: job.id, image: job.image, width: job.width, height: job.height }, [job.image]);
  }
}

// Convert any drawable source into transferable RGBA pixels.
function sourceToImageData(imageSource) {
  const w = imageSource.naturalWidth || imageSource.videoWidth || imageSource.width;
  const h = imageSource.naturalHeight || imageSource.videoHeight || imageSource.height;
  if (!w || !h) return null;
  if (typeof imageSource.getContext === 'function') {
    try { return imageSource.getContext('2d').getImageData(0, 0, w, h); } catch (e) { /* fallthrough */ }
  }
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d');
  ctx.drawImage(imageSource, 0, 0, w, h);
  return ctx.getImageData(0, 0, w, h);
}

async function detectFacesInWorker(imageSource) {
  const ok = await initWorkerPool();
  if (!ok) return null; // signal: use fallback
  const imageData = sourceToImageData(imageSource);
  if (!imageData) return [];
  const id = ++requestSeq;
  // Copy the buffer since getImageData's may be reused; transfer the copy.
  const buf = imageData.data.buffer.slice(0);
  return new Promise((resolve, reject) => {
    workerPool.queue.push({ id, image: buf, width: imageData.width, height: imageData.height, resolve, reject });
    drainWorkerQueue();
  });
}

function summarizeFaces(faces, detectTime) {
  if (!faces || faces.length === 0) {
    clientLogger.debug(`[FaceDetection] No faces detected (${detectTime}ms)`);
    return null;
  }
  clientLogger.debug(`[FaceDetection] Found ${faces.length} face(s) in ${detectTime}ms`);
  let minX = Infinity, minY = Infinity;
  let maxX = 0, maxY = 0;
  for (const face of faces) {
    const [x, y, w, h] = face.box;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + w);
    maxY = Math.max(maxY, y + h);
  }
  return {
    faceCount: faces.length,
    boundingBox: { minX, minY, maxX, maxY },
    faces: faces.map((f) => ({ box: f.box, score: f.score })),
  };
}

/**
 * Detect faces in an image and calculate optimal crop position
 * @param {HTMLImageElement|HTMLCanvasElement|ImageBitmap} imageSource - Image to analyze
 * @returns {Object|null} Crop coordinates { x, y, width, height } or null if no faces
 */
export async function detectFaces(imageSource) {
  const startTime = performance.now();
  // Preferred path: worker pool (parallel across photos, off the main thread)
  try {
    const faces = await detectFacesInWorker(imageSource);
    if (faces !== null) {
      return summarizeFaces(faces, Math.round(performance.now() - startTime));
    }
  } catch (error) {
    clientLogger.debug(`[FaceDetection] Worker detect failed, falling back in-thread: ${error.message}`);
  }
  // Fallback: in-thread singleton
  try {
    const h = await initHuman();
    const result = await h.detect(imageSource);
    return summarizeFaces(result.face || [], Math.round(performance.now() - startTime));
  } catch (error) {
    console.error('[FaceDetection] Detection error:', error);
    return null;
  }
}

/**
 * Calculate face-centered square crop coordinates
 * @param {number} imageWidth - Original image width
 * @param {number} imageHeight - Original image height
 * @param {Object} faceData - Face detection result from detectFaces()
 * @returns {Object} Crop coordinates { x, y, size }
 */
function calculateFaceCenteredCrop(imageWidth, imageHeight, faceData) {
  const cropSize = Math.min(imageWidth, imageHeight);

  if (!faceData || !faceData.boundingBox) {
    // No faces - use top-biased crop (rule of thirds) to preserve faces in upper portion
    const x = Math.floor((imageWidth - cropSize) / 2);
    const y = Math.floor((imageHeight - cropSize) * 0.33);
    return { x, y, size: cropSize, faceDetected: false };
  }

  const { minX, minY, maxX, maxY } = faceData.boundingBox;

  // Calculate center of all faces
  const faceCenterX = (minX + maxX) / 2;
  const faceCenterY = (minY + maxY) / 2;

  // Calculate crop position centered on faces
  let cropX = Math.round(faceCenterX - cropSize / 2);
  let cropY = Math.round(faceCenterY - cropSize / 2);

  // Clamp to image bounds
  cropX = Math.max(0, Math.min(cropX, imageWidth - cropSize));
  cropY = Math.max(0, Math.min(cropY, imageHeight - cropSize));

  clientLogger.debug(`[FaceDetection] Crop: x=${cropX}, y=${cropY}, size=${cropSize} (faces: ${faceData.faceCount})`);

  return {
    x: cropX,
    y: cropY,
    size: cropSize,
    faceDetected: true,
    faceCount: faceData.faceCount,
  };
}

/**
 * Detect dark bars (screenshot borders) by sampling pixels along edges
 * Returns the bounds of the actual content (excluding dark bars)
 *
 * @param {HTMLCanvasElement} canvas - Canvas with the image
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {number} width - Image width
 * @param {number} height - Image height
 * @returns {Object|null} Content bounds { top, bottom, left, right, width, height, hasBars }
 */
function detectContentBounds(canvas, ctx, width, height) {
  try {
    // Get raw pixel data
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data; // RGBA format

    // Helpers to check if a pixel is a border (dark or white)
    const isDarkPixel = (r, g, b) => r < DARK_THRESHOLD && g < DARK_THRESHOLD && b < DARK_THRESHOLD;
    const isWhitePixel = (r, g, b) => r > WHITE_THRESHOLD && g > WHITE_THRESHOLD && b > WHITE_THRESHOLD;
    const isBorderPixel = (r, g, b) => isDarkPixel(r, g, b) || isWhitePixel(r, g, b);

    // Sample multiple points across each row/column
    const sampleCount = 10;

    // Track border types for each edge
    const borderTypes = { top: null, bottom: null, left: null, right: null };

    // Detect top bar (scan from top down)
    let topBar = 0;
    let topBlackCount = 0, topWhiteCount = 0;
    for (let y = 0; y < height; y++) {
      let darkCount = 0;
      let blackInRow = 0, whiteInRow = 0;
      for (let i = 0; i < sampleCount; i++) {
        const x = Math.floor((width / sampleCount) * i + width / (sampleCount * 2));
        const idx = (y * width + x) * 4; // RGBA = 4 channels
        const r = data[idx], g = data[idx + 1], b = data[idx + 2];
        if (isDarkPixel(r, g, b)) { darkCount++; blackInRow++; }
        else if (isWhitePixel(r, g, b)) { darkCount++; whiteInRow++; }
      }
      // Row is a border if 80%+ samples are dark/white
      if (darkCount >= sampleCount * 0.8) {
        topBar = y + 1;
        topBlackCount += blackInRow;
        topWhiteCount += whiteInRow;
      } else {
        break;
      }
    }
    if (topBar > 0) {
      borderTypes.top = topBlackCount > topWhiteCount ? 'black' : (topWhiteCount > topBlackCount ? 'white' : 'mixed');
    }

    // Detect bottom bar (scan from bottom up)
    let bottomBar = 0;
    let bottomBlackCount = 0, bottomWhiteCount = 0;
    for (let y = height - 1; y >= 0; y--) {
      let darkCount = 0;
      let blackInRow = 0, whiteInRow = 0;
      for (let i = 0; i < sampleCount; i++) {
        const x = Math.floor((width / sampleCount) * i + width / (sampleCount * 2));
        const idx = (y * width + x) * 4;
        const r = data[idx], g = data[idx + 1], b = data[idx + 2];
        if (isDarkPixel(r, g, b)) { darkCount++; blackInRow++; }
        else if (isWhitePixel(r, g, b)) { darkCount++; whiteInRow++; }
      }
      if (darkCount >= sampleCount * 0.8) {
        bottomBar = height - y;
        bottomBlackCount += blackInRow;
        bottomWhiteCount += whiteInRow;
      } else {
        break;
      }
    }
    if (bottomBar > 0) {
      borderTypes.bottom = bottomBlackCount > bottomWhiteCount ? 'black' : (bottomWhiteCount > bottomBlackCount ? 'white' : 'mixed');
    }

    // Detect left bar (scan from left to right)
    let leftBar = 0;
    let leftBlackCount = 0, leftWhiteCount = 0;
    for (let x = 0; x < width; x++) {
      let darkCount = 0;
      let blackInCol = 0, whiteInCol = 0;
      for (let i = 0; i < sampleCount; i++) {
        const y = Math.floor((height / sampleCount) * i + height / (sampleCount * 2));
        const idx = (y * width + x) * 4;
        const r = data[idx], g = data[idx + 1], b = data[idx + 2];
        if (isDarkPixel(r, g, b)) { darkCount++; blackInCol++; }
        else if (isWhitePixel(r, g, b)) { darkCount++; whiteInCol++; }
      }
      if (darkCount >= sampleCount * 0.8) {
        leftBar = x + 1;
        leftBlackCount += blackInCol;
        leftWhiteCount += whiteInCol;
      } else {
        break;
      }
    }
    if (leftBar > 0) {
      borderTypes.left = leftBlackCount > leftWhiteCount ? 'black' : (leftWhiteCount > leftBlackCount ? 'white' : 'mixed');
    }

    // Detect right bar (scan from right to left)
    let rightBar = 0;
    let rightBlackCount = 0, rightWhiteCount = 0;
    for (let x = width - 1; x >= 0; x--) {
      let darkCount = 0;
      let blackInCol = 0, whiteInCol = 0;
      for (let i = 0; i < sampleCount; i++) {
        const y = Math.floor((height / sampleCount) * i + height / (sampleCount * 2));
        const idx = (y * width + x) * 4;
        const r = data[idx], g = data[idx + 1], b = data[idx + 2];
        if (isDarkPixel(r, g, b)) { darkCount++; blackInCol++; }
        else if (isWhitePixel(r, g, b)) { darkCount++; whiteInCol++; }
      }
      if (darkCount >= sampleCount * 0.8) {
        rightBar = width - x;
        rightBlackCount += blackInCol;
        rightWhiteCount += whiteInCol;
      } else {
        break;
      }
    }
    if (rightBar > 0) {
      borderTypes.right = rightBlackCount > rightWhiteCount ? 'black' : (rightWhiteCount > rightBlackCount ? 'white' : 'mixed');
    }

    // Add safety buffer (5px or 0.5% of dimension) to ensure we clear any gradient
    const safetyBuffer = Math.max(5, Math.min(width, height) * 0.005);
    const safeTop = topBar > 0 ? Math.ceil(topBar + safetyBuffer) : 0;
    const safeBottom = bottomBar > 0 ? Math.ceil(bottomBar + safetyBuffer) : 0;
    const safeLeft = leftBar > 0 ? Math.ceil(leftBar + safetyBuffer) : 0;
    const safeRight = rightBar > 0 ? Math.ceil(rightBar + safetyBuffer) : 0;

    // Check if bars are significant (more than 2% of image dimension)
    const minSignificantBar = Math.min(width, height) * 0.02;
    let hasBars = safeTop > minSignificantBar || safeBottom > minSignificantBar ||
                    safeLeft > minSignificantBar || safeRight > minSignificantBar;

    // Distinguish white studio backgrounds from actual screenshot white bars.
    // If all detected bars are white (no black/dark bars), sample the interior
    // of the image. If the interior is also predominantly white, this is a
    // white-background photo (e.g. studio portrait), not a screenshot.
    if (hasBars) {
      const significantTypes = [];
      if (safeTop > minSignificantBar && borderTypes.top) significantTypes.push(borderTypes.top);
      if (safeBottom > minSignificantBar && borderTypes.bottom) significantTypes.push(borderTypes.bottom);
      if (safeLeft > minSignificantBar && borderTypes.left) significantTypes.push(borderTypes.left);
      if (safeRight > minSignificantBar && borderTypes.right) significantTypes.push(borderTypes.right);

      const allWhite = significantTypes.length > 0 && significantTypes.every(t => t === 'white');

      if (allWhite) {
        // Sample interior pixels (center 50% region) to check for white background
        const interiorSamples = 20;
        let whiteInteriorCount = 0;
        const startX = Math.floor(width * 0.25);
        const endX = Math.floor(width * 0.75);
        const startY = Math.floor(height * 0.25);
        const endY = Math.floor(height * 0.75);
        const rangeX = endX - startX;
        const rangeY = endY - startY;

        for (let i = 0; i < interiorSamples; i++) {
          const sx = startX + Math.floor(rangeX * ((i * 7 + 3) % interiorSamples) / interiorSamples);
          const sy = startY + Math.floor(rangeY * ((i * 11 + 5) % interiorSamples) / interiorSamples);
          const idx = (sy * width + sx) * 4; // RGBA = 4 channels
          const r = data[idx], g = data[idx + 1], b = data[idx + 2];
          if (r > WHITE_THRESHOLD && g > WHITE_THRESHOLD && b > WHITE_THRESHOLD) {
            whiteInteriorCount++;
          }
        }

        const interiorWhiteRatio = whiteInteriorCount / interiorSamples;
        if (interiorWhiteRatio > 0.3) {
          clientLogger.debug(`[FaceDetection] White background detected (${Math.round(interiorWhiteRatio * 100)}% interior white) — suppressing border detection`);
          hasBars = false;
        }
      }
    }

    const contentWidth = width - safeLeft - safeRight;
    const contentHeight = height - safeTop - safeBottom;

    // Log detailed border type information
    if (hasBars) {
      const detectedBorders = [];
      if (safeTop > minSignificantBar) detectedBorders.push(`top: ${safeTop}px (${borderTypes.top})`);
      if (safeBottom > minSignificantBar) detectedBorders.push(`bottom: ${safeBottom}px (${borderTypes.bottom})`);
      if (safeLeft > minSignificantBar) detectedBorders.push(`left: ${safeLeft}px (${borderTypes.left})`);
      if (safeRight > minSignificantBar) detectedBorders.push(`right: ${safeRight}px (${borderTypes.right})`);
      clientLogger.debug(`[FaceDetection] Border types detected: ${detectedBorders.join(', ')}`);
    }

    return {
      top: safeTop,
      bottom: height - safeBottom,  // Actual row where content ends
      left: safeLeft,
      right: width - safeRight,     // Actual column where content ends
      width: contentWidth,
      height: contentHeight,
      hasBars,
      bars: { top: safeTop, bottom: safeBottom, left: safeLeft, right: safeRight },
      borderTypes  // Include border types in result
    };

  } catch (error) {
    clientLogger.debug(`[FaceDetection] Border detection failed: ${error.message}`);
    return null;
  }
}

/**
 * Adjust crop position to avoid black bars while keeping face centered
 *
 * @param {Object} contentBounds - Content bounds from detectContentBounds()
 * @param {Object} cropInfo - Crop info { x, y, size } from calculateFaceCenteredCrop
 * @param {number} imageWidth - Original image width
 * @param {number} imageHeight - Original image height
 * @returns {Object} Adjusted crop { x, y, size, zoomFactor }
 */
function adjustCropForBorders(contentBounds, cropInfo, imageWidth, imageHeight) {
  if (!contentBounds || !contentBounds.hasBars) {
    // No bars detected, no adjustment needed
    return { ...cropInfo, zoomFactor: 1.0 };
  }

  const cropSize = cropInfo.size;
  const contentSize = Math.min(contentBounds.width, contentBounds.height);

  // Get face center from crop position
  const faceCenterX = cropInfo.x + cropSize / 2;
  const faceCenterY = cropInfo.y + cropSize / 2;

  // If content is smaller than crop, we need to zoom in
  if (contentSize < cropSize) {
    // Calculate required zoom, capped at 2.5x to preserve quality
    const idealZoomFactor = cropSize / contentSize;
    const zoomFactor = Math.min(idealZoomFactor, 2.5);

    // When zoomed, the visible viewport is smaller
    const viewportSize = cropSize / zoomFactor;

    // Constrain face center to be within content bounds
    const constrainedFaceX = Math.max(
      contentBounds.left + viewportSize / 2,
      Math.min(contentBounds.right - viewportSize / 2, faceCenterX)
    );
    const constrainedFaceY = Math.max(
      contentBounds.top + viewportSize / 2,
      Math.min(contentBounds.bottom - viewportSize / 2, faceCenterY)
    );

    // Position crop centered on the constrained face position
    let adjustedX = Math.round(constrainedFaceX - cropSize / 2);
    let adjustedY = Math.round(constrainedFaceY - cropSize / 2);

    // Final bounds check
    adjustedX = Math.max(0, Math.min(imageWidth - cropSize, adjustedX));
    adjustedY = Math.max(0, Math.min(imageHeight - cropSize, adjustedY));

    clientLogger.debug(`[FaceDetection] Zoom needed: ${zoomFactor.toFixed(2)}x (content: ${contentSize}px, crop: ${cropSize}px)`);

    return {
      x: adjustedX,
      y: adjustedY,
      size: cropSize,
      zoomFactor,
      faceDetected: cropInfo.faceDetected,
      faceCount: cropInfo.faceCount,
    };
  }

  // Content is large enough - reposition crop to avoid bars while keeping face centered
  let { x, y } = cropInfo;

  // Try to keep face at center, but constrain to content bounds
  const idealX = faceCenterX - cropSize / 2;
  const idealY = faceCenterY - cropSize / 2;

  // Constrain to content bounds
  x = Math.max(contentBounds.left, idealX);
  y = Math.max(contentBounds.top, idealY);

  // Ensure crop doesn't extend into bottom/right bars
  if (x + cropSize > contentBounds.right) {
    x = contentBounds.right - cropSize;
  }
  if (y + cropSize > contentBounds.bottom) {
    y = contentBounds.bottom - cropSize;
  }

  // Final image bounds check
  x = Math.max(0, Math.min(imageWidth - cropSize, x));
  y = Math.max(0, Math.min(imageHeight - cropSize, y));

  const adjustedX = Math.round(x);
  const adjustedY = Math.round(y);

  // Log if adjustment was significant
  const moved = Math.abs(adjustedX - cropInfo.x) > 5 || Math.abs(adjustedY - cropInfo.y) > 5;
  if (moved) {
    clientLogger.debug(`[FaceDetection] Crop repositioned to avoid bars: (${cropInfo.x},${cropInfo.y}) → (${adjustedX},${adjustedY})`);
  }

  return {
    x: adjustedX,
    y: adjustedY,
    size: cropSize,
    zoomFactor: 1.0,
    faceDetected: cropInfo.faceDetected,
    faceCount: cropInfo.faceCount,
  };
}

/**
 * Create a face-centered cropped blob from an image file
 * This is the main function to use for instant face-centered preview
 * Also applies border detection to avoid black bars in screenshots
 *
 * @param {File} imageFile - Image file from input
 * @returns {Promise<{blob: Blob, url: string, cropData: Object}>} Cropped blob and URL
 */
export async function createFaceCenteredCrop(imageFile) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(imageFile);

    img.onload = async () => {
      try {
        const startTime = performance.now();

        // Analyze on a bounded-size canvas: face detection and border scanning
        // only need coarse geometry, and feeding them the full-resolution
        // bitmap is what made this cost 1-2s/photo (tensor conversion + a
        // full-res getImageData readback). Every result is mapped back to
        // full-image pixels, and the final crop still samples the full-res
        // img — output quality is unchanged. Border thresholds are
        // proportional (2% of min dimension), so they scale with the canvas.
        const ANALYZE_MAX = 800;
        const analyzeScale = Math.min(1, ANALYZE_MAX / Math.max(img.width, img.height));
        const analyzeW = Math.max(1, Math.round(img.width * analyzeScale));
        const analyzeH = Math.max(1, Math.round(img.height * analyzeScale));
        const analyzeCanvas = document.createElement('canvas');
        analyzeCanvas.width = analyzeW;
        analyzeCanvas.height = analyzeH;
        const analyzeCtx = analyzeCanvas.getContext('2d');
        analyzeCtx.drawImage(img, 0, 0, analyzeW, analyzeH);
        const upscale = (v) => Math.round(v / analyzeScale);

        // Detect faces (in analyze-canvas space -> mapped to full-image px)
        let faceData = await detectFaces(analyzeCanvas);
        if (faceData && analyzeScale < 1) {
          faceData = {
            ...faceData,
            boundingBox: {
              minX: upscale(faceData.boundingBox.minX),
              minY: upscale(faceData.boundingBox.minY),
              maxX: upscale(faceData.boundingBox.maxX),
              maxY: upscale(faceData.boundingBox.maxY),
            },
            faces: faceData.faces.map((f) => ({ ...f, box: f.box.map(upscale) })),
          };
        }

        // Calculate initial face-centered crop position
        let cropInfo = calculateFaceCenteredCrop(img.width, img.height, faceData);

        // Detect black bars/borders (on the analyze canvas -> full-image px)
        let contentBounds = detectContentBounds(analyzeCanvas, analyzeCtx, analyzeW, analyzeH);
        if (contentBounds && analyzeScale < 1) {
          contentBounds = {
            ...contentBounds,
            top: upscale(contentBounds.top),
            bottom: upscale(contentBounds.bottom),
            left: upscale(contentBounds.left),
            right: upscale(contentBounds.right),
            width: upscale(contentBounds.width),
            height: upscale(contentBounds.height),
            bars: {
              top: upscale(contentBounds.bars.top),
              bottom: upscale(contentBounds.bars.bottom),
              left: upscale(contentBounds.bars.left),
              right: upscale(contentBounds.bars.right),
            },
          };
        }

        // Adjust crop position to avoid borders.
        // Skip white-only border adjustment when faces are detected — face-centered
        // crop is already correct, and white borders are likely studio backgrounds.
        if (contentBounds && contentBounds.hasBars) {
          const hasBlackBars = contentBounds.borderTypes &&
            Object.values(contentBounds.borderTypes).some(t => t === 'black' || t === 'mixed');
          if (hasBlackBars || !cropInfo.faceDetected) {
            cropInfo = adjustCropForBorders(contentBounds, cropInfo, img.width, img.height);
          } else {
            clientLogger.debug('[FaceDetection] Skipping white border adjustment — faces detected, likely studio background');
          }
        }

        // Create canvas for final cropping
        const canvas = document.createElement('canvas');
        canvas.width = cropInfo.size;
        canvas.height = cropInfo.size;
        const ctx = canvas.getContext('2d');

        // When zoom is needed (zoomFactor > 1), we need to:
        // 1. Extract a smaller region (the viewport that fits within content)
        // 2. Scale it up to fill the crop size
        const zoomFactor = cropInfo.zoomFactor || 1.0;

        // Store viewport info for later use (will be included in cropData)
        let viewportInfo = null;

        if (zoomFactor > 1.0) {
          // Calculate the viewport size (smaller region that avoids bars)
          const viewportSize = Math.round(cropInfo.size / zoomFactor);

          // Calculate the center of the crop area
          const centerX = cropInfo.x + cropInfo.size / 2;
          const centerY = cropInfo.y + cropInfo.size / 2;

          // Calculate viewport position centered on the same point
          let viewportX = Math.round(centerX - viewportSize / 2);
          let viewportY = Math.round(centerY - viewportSize / 2);

          // Ensure viewport stays within image bounds
          viewportX = Math.max(0, Math.min(img.width - viewportSize, viewportX));
          viewportY = Math.max(0, Math.min(img.height - viewportSize, viewportY));

          // Store the viewport that was actually used for the crop
          viewportInfo = {
            x: viewportX,
            y: viewportY,
            width: viewportSize,
            height: viewportSize
          };

          clientLogger.debug(`[FaceDetection] Zoom ${zoomFactor.toFixed(2)}x: extracting ${viewportSize}px viewport at (${viewportX},${viewportY}), scaling to ${cropInfo.size}px`);

          // Draw smaller viewport region, scaled up to fill the canvas
          ctx.drawImage(
            img,
            viewportX, viewportY, viewportSize, viewportSize,  // Source: smaller viewport
            0, 0, cropInfo.size, cropInfo.size                  // Destination: full crop size
          );
        } else {
          // No zoom needed - draw cropped region normally
          ctx.drawImage(
            img,
            cropInfo.x, cropInfo.y, cropInfo.size, cropInfo.size,  // Source
            0, 0, cropInfo.size, cropInfo.size                      // Destination
          );
        }

        // Convert to blob
        canvas.toBlob((blob) => {
          if (!blob) {
            // Fallback to original if blob creation fails
            resolve({
              blob: imageFile,
              url: objectUrl,
              cropData: cropInfo,
              fallback: true,
            });
            return;
          }

          const croppedUrl = URL.createObjectURL(blob);
          const totalTime = Math.round(performance.now() - startTime);

          clientLogger.debug(`[FaceDetection] Created face-centered crop in ${totalTime}ms`);

          // Revoke the original object URL since we don't need it anymore
          URL.revokeObjectURL(objectUrl);

          resolve({
            blob,
            url: croppedUrl,
            cropData: {
              ...cropInfo,
              originalWidth: img.width,
              originalHeight: img.height,
              contentBounds: contentBounds, // Include border info for server
              viewport: viewportInfo, // The actual viewport used for the crop (null if no zoom)
            },
          });
        }, 'image/jpeg', 0.92);

      } catch (error) {
        console.error('[FaceDetection] Error creating crop:', error);
        // Return original image as fallback
        resolve({
          blob: imageFile,
          url: objectUrl,
          cropData: null,
          fallback: true,
          error: error.message,
        });
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image'));
    };

    img.src = objectUrl;
  });
}

/**
 * Pre-warm the Human library by loading models
 * Call this early to reduce first-image latency
 */
export async function warmupFaceDetection() {
  try {
    const poolOk = await initWorkerPool();
    if (!poolOk) await initHuman();
    clientLogger.debug(`[FaceDetection] Warmup complete (${poolOk ? 'worker pool' : 'in-thread'})`);
    return true;
  } catch (error) {
    console.error('[FaceDetection] Warmup failed:', error);
    return false;
  }
}

/**
 * Check if face detection is available
 */
export function isFaceDetectionAvailable() {
  return human !== null && human.models?.loaded();
}

const faceDetectionService = {
  createFaceCenteredCrop,
  warmupFaceDetection,
  isFaceDetectionAvailable,
};

export default faceDetectionService;
